/** biome-ignore-all lint/suspicious/noAssignInExpressions: reason */
/** biome-ignore-all lint/performance/noAccumulatingSpread: reason */

import { differenceInDays, isSameMonth, subMonths } from 'date-fns';

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

import { useAuth } from '../auth';
import { useFirebase } from '../Firebase';
import { getObjFromLink, transformCollectionSnap } from '../helpers';
import { useKegs } from '../kegs';

const isDev = process.env.NODE_ENV === 'development';

const ReportsContext = createContext({});

const monthsShort = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'June',
  'July',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec'
];

export const ReportsProvider = ({ children }) => {
  const firebase = useFirebase();
  const { claims } = useAuth();
  const { companyKegs } = useKegs();

  const activeListenersRef = React.useRef({});

  const [reports, setReports] = useState({
    reports_issue_kegs: null,
    reports_keg_movements: null,
    reports_kegs_bulk_update: null,
    reports_kegs_dispatched: null,
    reports_kegs_filled: null,
    reports_kegs_received: null,
    reports_partner_keg_movements: null,
    reports_warehouse_transfer: null
  });

  const companyLinkRef = React.useRef(claims?.companyLink);
  companyLinkRef.current = claims?.companyLink;

  const getReport = useCallback(
    (reportName, linkType) => {
      const companyLink = companyLinkRef.current;

      if (!companyLink) {
        if (isDev) {
          console.log(
            `[ReportsProvider] Skipping ${reportName} listener - no companyLink`
          );
        }

        return () => {};
      }

      const listenerKey = `${reportName}_${linkType}`;

      if (activeListenersRef.current[listenerKey]) {
        if (isDev) {
          console.log(
            `[ReportsProvider] Listener already active for ${reportName}, skipping duplicate`
          );
        }

        return () => {};
      }

      if (isDev) {
        console.log(
          `[ReportsProvider] Setting up ${reportName} listener for companyLink:`,
          companyLink
        );
      }

      activeListenersRef.current[listenerKey] = true;

      const unsubscribe = firebase
        .reports(reportName)
        .where(linkType, '==', companyLink)
        .onSnapshot(
          (snap) => {
            if (isDev) {
              console.log(
                `[ReportsProvider] ${reportName} snapshot received:`,
                {
                  count: snap.size
                }
              );
            }

            setReports((prev) => ({
              ...prev,
              [reportName]: transformCollectionSnap(snap)
            }));
          },
          (err) => {
            if (isDev) {
              console.error(`[ReportsProvider] ${reportName} error:`, err);
            }

            console.log(err);
          }
        );

      return () => {
        if (isDev) {
          console.log(`[ReportsProvider] Cleaning up ${reportName} listener`);
        }

        delete activeListenersRef.current[listenerKey];
        unsubscribe();
      };
    },
    [firebase]
  );

  const getPartnerReports = (setReports, reportName) => {
    return firebase
      .reports(reportName)
      .where('partnerLink', '==', claims.companyLink)
      .onSnapshot(
        (snap) => setReports(transformCollectionSnap(snap)),
        (err) => console.log(err)
      );
  };

  // Report listeners are now on-demand only — they start when a report page
  // or VolumeProvider calls getReport(), not on provider mount. This removes
  // 2-3 real-time Firestore listeners from the initial app load.

  const getReports = (setReports, reportName) => {
    return firebase
      .reports(reportName)
      .where('companyLink', '==', claims.companyLink)
      .onSnapshot(
        (snap) => setReports(transformCollectionSnap(snap)),
        (err) => console.log(err)
      );
  };

  const getDispatchedData = useCallback(
    (date) => {
      const monthReports =
        reports.reports_kegs_dispatched?.filter(
          (r) =>
            r.dateDispatched && isSameMonth(r.dateDispatched.toDate(), date)
        ) || [];

      const numKegs = monthReports.reduce(
        (tot, report) => tot + (report.kegsDispatched.length || 0),
        0
      );

      return numKegs;
    },
    [reports.reports_kegs_dispatched]
  );

  const getReceiveData = useCallback(
    (date) => {
      const monthReports =
        reports.reports_kegs_received?.filter(
          (r) => r.dateReceived && isSameMonth(r.dateReceived.toDate(), date)
        ) || [];

      const numKegs = monthReports.reduce(
        (tot, report) => tot + (report.kegsReceived.length || 0),
        0
      );

      return numKegs;
    },
    [reports.reports_kegs_received]
  );

  const monthData = useCallback(
    (date) => ({
      dispatched: getDispatchedData(date),
      month: monthsShort[date.getMonth()],
      monthNum: date.getMonth(),
      received: getReceiveData(date)
    }),
    [getDispatchedData, getReceiveData]
  );

  const dispatchReceiveCounts = useMemo(() => {
    const thisMonth = monthData(new Date());
    const month1 = monthData(subMonths(new Date(), 1));
    const month2 = monthData(subMonths(new Date(), 2));
    const month3 = monthData(subMonths(new Date(), 3));

    return {
      month1,
      month2,
      month3,
      thisMonth
    };
  }, [monthData]);

  const barData = useMemo(
    () => [
      dispatchReceiveCounts.month3,
      dispatchReceiveCounts.month2,
      dispatchReceiveCounts.month1
    ],
    [dispatchReceiveCounts]
  );

  const pieData = useMemo(() => {
    const dispatchReports =
      reports.reports_keg_movements?.filter(
        (r) =>
          r.dateDispatched &&
          (isSameMonth(r.dateDispatched.toDate(), subMonths(new Date(), 1)) ||
            isSameMonth(r.dateDispatched.toDate(), subMonths(new Date(), 2)) ||
            isSameMonth(r.dateDispatched.toDate(), subMonths(new Date(), 3)))
      ) || [];

    const brandCounts = dispatchReports.reduce((obj, report) => {
      if (obj[report.kegDetails.brandLink] > 0) {
        return {
          ...obj,
          [report.kegDetails.brandLink]: obj[report.kegDetails.brandLink] + 1
        };
      } else {
        return {
          ...obj,
          [report.kegDetails.brandLink]: 1
        };
      }
    }, {});

    const total = Object.values(brandCounts).reduce(
      (tot, val) => (tot += val),
      0
    );

    let otherCount = 0;
    let numOther = 0;

    return Object.entries(brandCounts)
      .map(([key, value]) => ({
        id: key,
        label: getObjFromLink(key).name,
        value
      }))
      .sort((a, b) => (a.value > b.value ? -1 : 1))
      .reduce((arr, item) => {
        if (item.value / total > 0.025) {
          return [...arr, item];
        } else {
          numOther++;

          const otherItem = arr.find((i) => i.id === `Other${otherCount}`);
          const filtered = arr.filter((i) => i.id !== `Other${otherCount}`);

          if (numOther === 19) {
            otherCount++;
            numOther = 0;
          }

          const other = otherItem
            ? {
                ...otherItem,
                label: (
                  <>
                    {otherItem.label}
                    <br />
                    <span>
                      {item.label} <strong>{item.value}</strong>
                    </span>
                  </>
                ),
                value: otherItem.value + item.value
              }
            : {
                id: `Other${otherCount}`,
                label: (
                  <span>
                    {item.label} <strong>{item.value}</strong>
                  </span>
                ),
                value: item.value
              };

          return [...filtered, other];
        }
      }, [])
      .filter((b) => {
        return b.value > 0;
      })
      .sort((a, b) => (a.value > b.value ? -1 : 1));
  }, [reports.reports_keg_movements]);

  const stockAtClient = useMemo(() => {
    const atClient =
      companyKegs?.filter((k) => k.locationStatus === 'dispatched__client') ||
      [];

    const over30 = atClient.filter((keg) => {
      return (
        differenceInDays(new Date(), keg.dateDispatched.toDate()) > 30 &&
        differenceInDays(new Date(), keg.dateDispatched.toDate()) <= 60
      );
    });

    const over60 = atClient.filter((keg) => {
      return differenceInDays(new Date(), keg.dateDispatched.toDate()) > 60;
    });

    return {
      atClient,
      over30,
      over60
    };
  }, [companyKegs]);

  return (
    <ReportsContext.Provider
      value={{
        getPartnerReports,
        getReports,
        ...reports,
        barData,
        // cspell:disable-next-line
        dispatchRecieveCounts: dispatchReceiveCounts,
        getReport,
        pieData,
        stockAtClient
      }}
    >
      {children}
    </ReportsContext.Provider>
  );
};

export const ReportsConsumer = ReportsContext.Consumer;

export const useReports = () => useContext(ReportsContext);
